import { CURRENCIES } from "../pages/website/contractManufacturerRfqFormState";

export function resolveOptionLabel(rawValue, options = []) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  const list = Array.isArray(options) ? options : [];
  const byValue = list.find((opt) => String(opt?.value ?? "").trim() === raw);
  if (byValue?.label) return String(byValue.label).trim();
  const byLabel = list.find(
    (opt) => String(opt?.label ?? "").trim().toLowerCase() === raw.toLowerCase(),
  );
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

export function resolveUnitLabel(rawUnit, ...optionSets) {
  const raw = String(rawUnit || "").trim();
  if (!raw) return "";
  const allOptions = optionSets.flat().filter(Boolean);
  const byValue = allOptions.find((opt) => String(opt?.value) === raw);
  if (byValue?.label) return String(byValue.label).trim();
  const byLabel = allOptions.find(
    (opt) => String(opt?.label || "").toLowerCase() === raw.toLowerCase(),
  );
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

export function resolveCurrencyLabel(rawValue, currencyOptions = []) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  const opts =
    Array.isArray(currencyOptions) && currencyOptions.length
      ? currencyOptions
      : CURRENCIES.map((c) => ({ value: c, label: c, currencyCode: c }));

  const byValue = opts.find((opt) => String(opt?.value ?? "").trim() === raw);
  if (byValue) {
    return String(byValue.currencyCode || byValue.label || raw).trim();
  }
  const byCode = opts.find(
    (opt) =>
      String(opt?.currencyCode || opt?.label || "")
        .trim()
        .toUpperCase() === raw.toUpperCase(),
  );
  if (byCode) {
    return String(byCode.currencyCode || byCode.label || raw).trim();
  }
  const byLabel = opts.find(
    (opt) => String(opt?.label || "").trim().toLowerCase() === raw.toLowerCase(),
  );
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

function formatYesNo(value) {
  const s = String(value).trim().toLowerCase();
  if (s === "yes" || s === "true" || s === "1") return "Yes";
  if (s === "no" || s === "false" || s === "0") return "No";
  return String(value);
}

/** Map stored RFQ field values to buyer-facing labels (master ids → names). */
export function createCmRfqDisplayResolver({
  shippingTermsOptions = [],
  paymentScheduleOptions = [],
  paymentMethodOptions = [],
  modeOfTransportOptions = [],
  dimensionUnitOptions = [],
  attributeUnitOptions = [],
  currencyOptions = [],
} = {}) {
  const resolveScalar = (key, value) => {
    if (value == null || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
      const parts = value
        .map((item) => resolveScalar(key, item))
        .filter((part) => part && part !== "—");
      return parts.length ? parts.join(", ") : "—";
    }
    if (typeof value === "object") return JSON.stringify(value);

    switch (key) {
      case "incoterms":
        return resolveOptionLabel(value, shippingTermsOptions) || String(value);
      case "paymentTerms":
        return resolveOptionLabel(value, paymentScheduleOptions) || String(value);
      case "paymentMethod":
        return resolveOptionLabel(value, paymentMethodOptions) || String(value);
      case "preferredTransport":
        return resolveOptionLabel(value, modeOfTransportOptions) || String(value);
      case "deliveryMethods": {
        const resolved = resolveOptionLabel(value, modeOfTransportOptions);
        return resolved && resolved !== String(value).trim() ? resolved : String(value);
      }
      case "unitOfMeasurement":
      case "uom":
        return (
          resolveUnitLabel(value, attributeUnitOptions, dimensionUnitOptions) || String(value)
        );
      case "currency":
      case "sampleCurrency":
        return resolveCurrencyLabel(value, currencyOptions) || String(value);
      case "ndaRequired":
      case "priceNegotiable":
      case "toolingNrePossible":
      case "sampleOffered":
      case "coldChain":
      case "formulaRecipe":
        return formatYesNo(value);
      default:
        return String(value);
    }
  };

  return (key, value) => {
    const out = resolveScalar(key, value);
    return out == null || out === "" ? "—" : out;
  };
}
